import { useState, type ComponentProps, type ReactNode } from "react";
import { toast } from "sonner";
import { ActionPopover } from "@/components/action-popover";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/loading-button";
import { formatApiErrorMessage } from "@/lib/api-error";
import type { useResolveAction } from "./hooks";

/** Dismiss opens a short optional note, saved with the dismissal (USA-1268). */
export function DismissActionButton({
  actionId,
  resolveAction,
  variant,
  disabled,
  children,
}: {
  actionId: number;
  resolveAction: ReturnType<typeof useResolveAction>;
  variant: ComponentProps<typeof Button>["variant"];
  disabled?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <ActionPopover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <LoadingButton
          size="sm"
          variant={variant}
          loading={
            resolveAction.isPending &&
            resolveAction.variables?.status === "dismissed"
          }
          loadingText="Dismissing..."
          disabled={disabled}
        >
          {children}
        </LoadingButton>
      }
      note={{ label: "Dismissal note", placeholder: "Why dismiss? (optional)" }}
      confirmLabel="Dismiss"
      onConfirm={(notes, close) => {
        // Closes at once; the note is cleared only once the dismissal saves.
        setOpen(false);
        resolveAction.mutate(
          notes
            ? { actionId, status: "dismissed", notes }
            : { actionId, status: "dismissed" },
          {
            onSuccess: close,
            onError: (error) =>
              toast.error(formatApiErrorMessage("Action not dismissed", error)),
          },
        );
      }}
    />
  );
}
